import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import ProviderStatementBalanceEmailTemplate from 'models/core/providerStatementBalanceEmailTemplate/Model.ProviderStatementBalanceEmailTemplate';
import providerStatementBalanceEmailTemplateDeleteApi from 'models/core/providerStatementBalanceEmailTemplate/delete/Api.ProviderStatementBalanceEmailTemplate.Delete';
import providerStatementBalanceEmailTemplateSaveApi from 'models/core/providerStatementBalanceEmailTemplate/save/Api.ProviderStatementBalanceEmailTemplate.Save';
import { useMemo } from 'react';
import { providerStatementBalanceEmailTemplateIndexRoute } from './ProviderStatementBalanceEmailTemplate.Index';
import Provider from 'models/productManagement/provider/Model.Provider';
import StatementBalanceEmailTemplate from 'models/core/statementBalanceEmailTemplate/Model.StatementBalanceEmailTemplate';

export enum StatementBalanceEmailTriggers {
  SendEmail = 'ANYSTATUS_SEND_EMAIL',
}

export enum ProviderStatementBalanceEmailTemplateAction {
  EDIT = 'edit',
  DELETE = 'delete',
}
export type VerifyProviderStatementBalanceEmailTemplateAction = Utils.VerifyExtends<
  Module.Sales.ProviderStatementBalanceEmailTemplate.Actions,
  Utils.ValueOf<typeof ProviderStatementBalanceEmailTemplateAction>
>;
export default function useProviderStatementBalanceEmailTemplate() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<
    Module.Sales.ProviderStatementBalanceEmailTemplate.Params,
    Module.Sales.ProviderStatementBalanceEmailTemplate.Query
  >({
    route: providerStatementBalanceEmailTemplateIndexRoute,
  });

  const canView =
    permissions.CREATE_EMAILTEMPLATE || permissions.EDIT_EMAILTEMPLATE || permissions.DELETE_EMAILTEMPLATE;
  const templateApi = useApi({ action: ProviderStatementBalanceEmailTemplate.list });

  const listApi = useApi({
    action: ProviderStatementBalanceEmailTemplate.list,
    default: { limit: defaultLimit, offset: defaultOffset },
    body: pageRouter.query.list,
    callback: (req) => {
      templateApi.execute((b) => b);
      pageRouter.redirect({}, { ...pageRouter.query, list: req });
    },
    wait: !canView,
  });

  const deleteApi = useApi({
    action: providerStatementBalanceEmailTemplateDeleteApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const saveApi = useApi({
    action: providerStatementBalanceEmailTemplateSaveApi,
    wait: true,
  });

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const availableStatuses = useMemo(() => {
    const statementStatuses = Object.values({ ...StatementBalanceEmailTriggers })?.map((status) => status);
    const usedStatuses = templateApi?.payload?.data?.map((it) => it.statementBalanceStatusTrigger) || [];

    return [
      ...statementStatuses.filter((status: any) => !usedStatuses.includes(status)),
      ...[selected?.statementBalanceStatusTrigger],
    ].filter(Boolean);
  }, [templateApi?.payload?.data, selected]);

  const providerListApi = useApi({ action: Provider.list });
  const { payload: statementBalanceEmailTemplates } = useApi(
    {
      action: StatementBalanceEmailTemplate.list,
      wait: !availableStatuses,
      body: { statementBalanceStatus: availableStatuses },
    },
    [availableStatuses]
  );

  return {
    canView,
    pageRouter,
    listApi,
    saveApi,
    deleteApi,
    selected,
    permissions,
    availableStatuses,
    statementBalanceEmailTemplates,
    providerListApi,
    views: {
      [ProviderStatementBalanceEmailTemplateAction.DELETE]: !!(
        pageRouter.params.action === ProviderStatementBalanceEmailTemplateAction.DELETE &&
        selected &&
        permissions.DELETE_EMAILTEMPLATE
      ),
      [ProviderStatementBalanceEmailTemplateAction.EDIT]: !!(
        pageRouter.params.action === ProviderStatementBalanceEmailTemplateAction.EDIT &&
        ((permissions.CREATE_EMAILTEMPLATE && !selected) || (permissions.EDIT_EMAILTEMPLATE && selected))
      ),
    },
  };
}

export function useProviderStatementBalanceEmailTemplateProvider() {
  return useDataProvider<ReturnType<typeof useProviderStatementBalanceEmailTemplate>>();
}
